import type { AssignmentRuleMeta } from '@tsagko/label-contracts'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAdapter, useAdapterMode } from '../adapters/AdapterProvider'

export function RulesGallery() {
  const { t } = useTranslation()
  const adapter = useAdapter()
  const mode = useAdapterMode()
  const [items, setItems] = useState<AssignmentRuleMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    if (!adapter) return
    let cancelled = false
    void (async () => {
      const res = await adapter.resources.listRules({ limit: 100 })
      if (cancelled) return
      if (res.status === 'ok') setItems(res.data.items)
      else setError(res.error.message)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [adapter])

  if (!adapter || loading) {
    return <div className="p-8 text-sm text-stone-500">{t('gallery.rules.loading')}</div>
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{t('gallery.rules.title')}</h2>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {t(
              mode === 'embedded' ? 'gallery.rules.subtitleEmbedded' : 'gallery.rules.subtitle',
              { count: items.length }
            )}
          </p>
        </div>
        <Link
          to="/rules/new"
          data-testid="gallery-new-button"
          className="rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
        >
          {t('gallery.rules.newRule')}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded border border-rose-300 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div
          data-testid="gallery-zero-state"
          className="rounded border border-dashed border-stone-300 p-12 text-center dark:border-stone-700"
        >
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {t('gallery.rules.emptyState')}
          </p>
          <Link
            to="/rules/new"
            data-testid="gallery-zero-state-new"
            className="mt-4 inline-block rounded bg-stone-900 px-4 py-2 text-sm text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            {t('gallery.rules.newRule')}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <Link
            to="/rules/new"
            className="flex aspect-[5/3] items-center justify-center rounded border-2 border-dashed border-stone-300 text-sm text-stone-500 hover:border-stone-500 dark:border-stone-700 dark:text-stone-400"
          >
            {t('gallery.templates.startFromBlank')}
          </Link>
          {items.map(item => (
            <Link
              key={item.id}
              to={`/rules/${encodeURIComponent(item.id)}`}
              data-testid="gallery-item"
              data-name={item.name}
              className="flex aspect-[5/3] flex-col justify-between rounded border border-stone-200 bg-white p-4 text-left shadow-sm transition hover:border-stone-400 dark:border-stone-800 dark:bg-stone-900"
            >
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">{item.name}</h3>
                  {item.enabled === false && (
                    <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-stone-500 dark:bg-stone-800 dark:text-stone-400">
                      {t('gallery.rules.disabledBadge')}
                    </span>
                  )}
                </div>
                {item.description && (
                  <p className="mt-2 text-xs text-stone-600 dark:text-stone-400 line-clamp-3">
                    {item.description}
                  </p>
                )}
              </div>
              <p className="text-[10px] text-stone-400">
                priority {item.priority ?? 0} · {item.updatedAt?.slice(0, 10) ?? '—'}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
